"use client";

import { useCallback, useEffect, useMemo } from "react";

import { useTranslations } from "next-intl";

import { formStepConfig } from "@/config/formSteps.config";
import { DealType } from "@/enums/dealType";
import { useSession } from "@/providers/SessionProvider";
import { useAddObjectFormStore } from "@/store/addObjectForm.store";
import { Step } from "@/types/form.types";
import { getSafeDealType, getSafePropertyType } from "@/ui/AddObject/helpers";
import { FormStepLabel } from "@/ui/AddObject/schemas/form-steps.enum";

// Export Step interface for external use

// Export types
export interface UseFormStepsProps {
  externalStep?: number;
  onStepChange?: (step: number) => void;
  mode?: "create" | "edit";
  slug?: string;
}

export interface UseFormStepsReturn {
  steps: Step[];
  handleStepClick: (stepId: string) => void;
  step: number;
  totalSteps: number;
  nextStep: () => void;
  prevStep: () => void;
  setStep: (step: number) => void;
  setTotalSteps: (steps: number) => void;
  stepId: string | null;
  setStepById: (stepId: string) => void;
  goToStep: (step: number) => void;
  isLastStep: boolean;
  currentStep: number;
  currentStepId: string | null;
}

// Define roles that are permitted to see the Realtor step. These values must
// correspond to the backend `UserRole` / `AdminRole` string literals returned
// in the JWT payload/userInfo object. Update here if the backend changes.
const REALTOR_ROLES = [
  "user_realtor",
  "super_admin",
  "admin",
  "main_realtor",
  "realtor",
  "secretary",
] as const; // immutable tuple

export const useFormSteps = ({
  externalStep,
  onStepChange,
  mode = "create",
}: UseFormStepsProps = {}): UseFormStepsReturn => {
  const t = useTranslations("profile");
  const { hasRole } = useSession();

  const {
    currentStepId: storeStepId,
    setCurrentStepId: setStoreStepId,
    totalSteps,
    setTotalSteps,
    formData,
  } = useAddObjectFormStore();

  const dealType = formData.dealType || DealType.PRIMARY;
  const propertyType = formData.propertyType;

  const steps = useMemo<Step[]>(() => {
    const hasRealtorAccess = () => REALTOR_ROLES.some((role) => hasRole(role));

    const stepOrder = formStepConfig.getStepOrder(
      getSafeDealType(dealType),
      getSafePropertyType(propertyType),
    );

    const filtered = stepOrder
      .map((stepId) => {
        const config = formStepConfig.getStepById(stepId);
        if (!config) return null;

        const isVisible = config.isVisible
          ? config.isVisible(
              getSafeDealType(dealType),
              getSafePropertyType(propertyType),
            )
          : true;

        if (!isVisible) return null;
        if (stepId === FormStepLabel.REALTOR && !hasRealtorAccess()) {
          return null;
        }

        return {
          id: config.label,
          label: t(config.label),
        };
      })
      .filter(Boolean) as { id: string; label: string }[];

    return filtered.map((step, index) => ({
      ...step,
      status: index === 0 ? "active" : "disabled", // статус оновимо нижче
    }));
  }, [dealType, propertyType, hasRole, t]);

  useEffect(() => {
    if (steps.length !== totalSteps) {
      setTotalSteps(steps.length);
    }
  }, [steps.length, totalSteps, setTotalSteps]);

  const currentStepIndex = useMemo(() => {
    if (!storeStepId) return 0;

    const index = steps.findIndex((s) => s.id === storeStepId);

    return index === -1 ? 0 : index;
  }, [steps, storeStepId]);

  const step = externalStep !== undefined ? externalStep : currentStepIndex + 1;

  const stepId = useMemo(() => {
    if (storeStepId) return storeStepId;

    if (steps[0]) {
      setStoreStepId(steps[0].id);
      return steps[0].id;
    }

    return null;
  }, [storeStepId, steps, setStoreStepId]);

  const stepsWithStatus = useMemo<Step[]>(() => {
    return steps.map((s, index) => {
      if (index === currentStepIndex) {
        return { ...s, status: "active" };
      }
      if (index < currentStepIndex) {
        return { ...s, status: "completed" };
      }
      if (mode === "edit") {
        return { ...s, status: "completed" };
      }
      return { ...s, status: "disabled" };
    });
  }, [steps, currentStepIndex, mode]);

  const goToStep = useCallback(
    (stepNumber: number) => {
      const index = stepNumber - 1;
      if (!steps[index]) return;

      setStoreStepId(steps[index].id);
      onStepChange?.(stepNumber);
    },
    [steps, setStoreStepId, onStepChange],
  );

  const setStepById = useCallback(
    (id: string) => {
      const index = steps.findIndex((s) => s.id === id);
      if (index === -1) return;

      setStoreStepId(id);
      onStepChange?.(index + 1);
    },
    [steps, setStoreStepId, onStepChange],
  );

  const handleStepClick = useCallback(
    (id: string) => {
      setStepById(id);
    },
    [setStepById],
  );

  const nextStep = useCallback(() => {
    const nextIndex = Math.min(currentStepIndex + 1, steps.length - 1);
    setStoreStepId(steps[nextIndex].id);
  }, [currentStepIndex, steps, setStoreStepId]);

  const prevStep = useCallback(() => {
    const prevIndex = Math.max(currentStepIndex - 1, 0);
    setStoreStepId(steps[prevIndex].id);
  }, [currentStepIndex, steps, setStoreStepId]);

  const isLastStep = step === steps.length;

  return {
    steps: stepsWithStatus,
    handleStepClick,
    step,
    totalSteps,
    nextStep,
    prevStep,
    setStep: goToStep,
    setTotalSteps,
    stepId,
    setStepById,
    goToStep,
    isLastStep,
    currentStep: step,
    currentStepId: stepId,
  };
};

/**
 * Get step ID for a given step number
 * Re-exported from formSteps.config for backward compatibility
 */
export const getStepIdForStepNumber = (
  step: number,
  dealType: DealType | string,
  propertyType?: string | undefined,
): string => {
  return formStepConfig.getStepIdForStepNumber(
    step,
    getSafeDealType(dealType),
    getSafePropertyType(propertyType),
  );
};
